import { useEffect, useState } from 'react';

/**
 * Hooks de viewport (ADR 0017). Sur mobile, l'ouverture du clavier virtuel modifie
 * l'espace réellement visible de façon différente selon l'OS. La seule grandeur
 * portable qui « voit » le clavier est `window.visualViewport` — `100dvh` ne rétrécit
 * PAS au clavier sur iOS. Ces hooks alimentent le shell mobile anti-clavier.
 */

export interface VisualViewportState {
  /** Hauteur du viewport visuel (px), ou `null` si l'API est absente (fallback CSS). */
  height: number | null;
  /** Décalage vertical du viewport visuel (iOS scrolle sous le clavier au focus). */
  offsetTop: number;
}

function readViewport(): VisualViewportState {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  return vv ? { height: vv.height, offsetTop: vv.offsetTop } : { height: null, offsetTop: 0 };
}

/**
 * Suit le viewport VISUEL (`window.visualViewport`) : hauteur + décalage, mis à jour
 * au `resize`/`scroll` (apparition/disparition du clavier, barre d'adresse). Renvoie
 * `height: null` si l'API n'existe pas (vieux navigateurs, jsdom) → l'appelant retombe
 * sur `100dvh`.
 */
export function useVisualViewport(): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(readViewport);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => setState({ height: vv.height, offsetTop: vv.offsetTop });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return state;
}

/**
 * `matchMedia` réactif : `true` si la requête média correspond, ré-évalué au changement.
 * Sûr côté SSR/jsdom (retourne `false` si `matchMedia` est absent). Sert à choisir le
 * layout AU NIVEAU REACT (ex. shell mobile vs colonne desktop) — plutôt qu'en CSS, pour
 * ne monter qu'un seul arbre (un seul canevas, un seul abonnement au flux de dessin).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
